import { Button, Code, Group, Loader, Stack, Table, Text } from "@mantine/core";
import { useInfiniteQuery } from "@tanstack/react-query";

import {
  getListStaffAuditQueryKey,
  listStaffAudit,
} from "../../api/generated/endpoints/staff/staff";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { formatDateTime } from "../../ui/format";

const ACTION_LABEL: Record<string, string> = {
  "team.create": "Created a team",
  "credits.grant": "Granted credits",
  "credits.adjust": "Adjusted credits",
  "job.cancel": "Canceled a job",
  "user.delete": "Deleted a user",
  // G9's switch (issue 433), the AuditAction w5's PR 2 adds.
  "ai_task.model_set": "Switched an AI task's model",
  // Usage analytics' audited read of a generation's trace (its PR 8).
  "usage.trace_read": "Read a generation's trace",
};

/** The staff audit log (A7), newest first, a page at a time. */
export function StaffAuditScreen() {
  const audit = useInfiniteQuery({
    queryKey: [...getListStaffAuditQueryKey(), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listStaffAudit({ limit: 50, ...(pageParam === undefined ? {} : { after: pageParam }) }, {
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const entries = audit.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <Stack gap="sm">
      {audit.isPending ? <Loader size="sm" aria-label="Loading the audit log" /> : null}
      {audit.isError ? <ErrorAlert error={audit.error} /> : null}
      {audit.isSuccess && entries.length === 0 ? <Text>Nothing has been done yet.</Text> : null}
      {entries.length === 0 ? null : (
        <Table.ScrollContainer minWidth={640}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>When</Table.Th>
                <Table.Th>Action</Table.Th>
                <Table.Th>Target</Table.Th>
                <Table.Th>By</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entries.map((entry) => (
                <Table.Tr key={entry.id}>
                  <Table.Td>{formatDateTime(entry.created_at)}</Table.Td>
                  <Table.Td>{ACTION_LABEL[entry.action] ?? entry.action}</Table.Td>
                  <Table.Td>
                    <Code>{JSON.stringify(entry.target)}</Code>
                  </Table.Td>
                  <Table.Td>
                    <Code>{entry.actor_user_id}</Code>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {audit.hasNextPage ? (
        <Group>
          <Button
            variant="light"
            loading={audit.isFetchingNextPage}
            onClick={() => void audit.fetchNextPage()}
          >
            Load more
          </Button>
        </Group>
      ) : null}
    </Stack>
  );
}
